import React from 'react';
import { Button, Group, IconButton, Spinner, Table3, TableSettings, useToast } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useCurrentAgreement, useCurrentLocale } from '@economic/react-app';
import { EntryTypeIcon } from '~/app/shared/components/ui/EntryTypeIcon';
import { multipleDimensionsColumnRenderer } from '~/app/shared/dimensions/formatters';
import useDimensions from '~/app/shared/dimensions/hooks/useDimensions';
import { KVTModel, LedgerLineModel, LedgerLinesFilter, LedgerLinesModel } from './types';
import useQuery from '../../hooks/useQuery';
import { usePostQuery } from '../../hooks/usePostQuery';
import number from '../../util/utilities/number';
import KVTDialog from './KVTDialog';
import { useDialog } from '../Dialog/hooks/useDialog';
import { downloadFile } from '../../util/utilities/downloadFile';
import internalApiClient from '../../util/api/internalApiClient';
import { getExportColumns, getExportHeadings } from './utils';

interface TransactionsTableProps {
    hasDimension: boolean;
    hasAccessProjectUser: boolean;
    entryId: number;
    currency: string;
    showButtons?: boolean;
}

export default function TransactionsTable({
    hasDimension,
    hasAccessProjectUser,
    entryId,
    currency,
    showButtons = false,
}: TransactionsTableProps) {
    const toast = useToast();
    const locale = useCurrentLocale();
    const kvtQuery = useQuery<KVTModel>(`/reports/entry-transactions/${entryId}/kvt`, [], {
        useCache: false,
    });

    const ledgerLinesFilter: LedgerLinesFilter = {
        orderBy: 1,
        entryId,
        attachmentNumber: 0,
        date: null,
    };

    const reportQuery = usePostQuery<LedgerLinesModel>(`/reports/entry-transactions`, ledgerLinesFilter);
    const dimensionsQuery = useDimensions({ skip: !hasDimension });
    const currentAgreement = useCurrentAgreement();

    const [showKVT] = useDialog(KVTDialog);
    const defaultSettings: TableSettings = { rowHeight: 'short' };
    const emptyFormatableNumber = (value: any) => (value ? number.format(value) : '0,00');

    if (reportQuery.loading || kvtQuery.loading || dimensionsQuery.loading) return <Spinner />;

    const handleExport = async () => {
        const toastRef = toast.loading(t`Exporting to excel`);
        const headlines = getExportHeadings(currentAgreement, ledgerLinesFilter);

        const columns = getExportColumns(hasDimension, locale.currencyCode, currency, hasAccessProjectUser, dimensionsQuery);

        const excelFilename = `${reportQuery.data!.voucherHeadLine}.xlsx`;
        const response = await internalApiClient.post(
            `/reports/entry-transactions/export`,
            {
                filter: ledgerLinesFilter,
                columns,
                headlines,
            },
            {
                responseType: 'blob',
            }
        );

        downloadFile(response, excelFilename);
        toastRef.close();
    };

    const kvtDialog = (actionType: 0 | 1 | 2) => {
        showKVT({ entryId, actionType, kvtQueryData: kvtQuery.data });
    };

    const toolbarRight = (
        <Group>
            <IconButton
                aria-label={t`Export to Excel`}
                tooltip={t`Export to Excel`}
                icon="export-to-excel"
                onClick={handleExport}
            />
        </Group>
    );

    return (
        <>
            <h1>{reportQuery.data!.voucherHeadLine}</h1>
            {reportQuery.data!.canCopyTurnReverseVoucher && showButtons && (
                <Group className="justify-start">
                    <Button appearance="primary" onClick={() => kvtDialog(2)}>
                        <Trans id="kvt-reverse">Reverse</Trans>
                    </Button>
                    <Button onClick={() => kvtDialog(0)}>
                        <Trans id="kvt-copy">Copy</Trans>
                    </Button>
                    <Button onClick={() => kvtDialog(1)}>
                        <Trans id="kvt-turn">Turn</Trans>
                    </Button>
                </Group>
            )}
            <Table3<LedgerLineModel>
                data={reportQuery.data!.ledgerLines}
                defaultSettings={defaultSettings}
                toolbarRight={toolbarRight}
                enableFooter
                enablePrinting
                enableSorting
                id="reports-transactions-table">
                <Table3.Column<LedgerLineModel>
                    id="journalEntryType"
                    header={t`Type`}
                    renderer={value => <EntryTypeIcon entryType={value} />}
                />
                <Table3.Column<LedgerLineModel> id="accountNumber" header={t`Account`} />
                <Table3.Column<LedgerLineModel> id="debitCreditNumber" header={t`Customer/Supplier`} />
                <Table3.Column<LedgerLineModel> id="text" header={t`Text`} />
                <Table3.Column<LedgerLineModel> id="vatCode" header={t`VAT`} />
                <Table3.Column<LedgerLineModel> id="currency" header={t`Currency`} />
                <Table3.Column<LedgerLineModel>
                    id="balance"
                    header={t`Amount`}
                    align="right"
                    renderer={value => <span>{emptyFormatableNumber(value)}</span>}
                />
                <Table3.Column<LedgerLineModel>
                    id="balanceInDKK"
                    header={`${t`Amount`} (${currency})`}
                    align="right"
                    renderer={value => <span>{emptyFormatableNumber(value)}</span>}
                />
                <Table3.Column<LedgerLineModel>
                    id="calculatedBalance"
                    header={`${t`Balance`} (${currency})`}
                    align="right"
                    renderer={value => <span>{emptyFormatableNumber(value)}</span>}
                />
                {hasAccessProjectUser && <Table3.Column<LedgerLineModel> id="projectNumber" header={t`Project`} />}
                {hasDimension ? (
                    <Table3.Column<LedgerLineModel>
                        id="dimensions"
                        header={t`Dimensions`}
                        renderer={value =>
                            multipleDimensionsColumnRenderer(
                                { allDimensions: dimensionsQuery.data, areAllDimensionsLoading: dimensionsQuery.loading },
                                value
                            )
                        }
                    />
                ) : null}
            </Table3>
        </>
    );
}
